import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { ClipboardCheck, Play, Plus, Package, PackagePlus, Undo2 } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

export default function Index({ takes, active_item_count, curaprox_item_count, pending_suggestion_count, default_counted_by, is_owner, pin_mode }) {
  const [countedBy, setCountedBy] = useState(default_counted_by === 'Staff' ? '' : (default_counted_by || ''))
  const [starting, setStarting] = useState('') // '' | 'full' | 'curaprox'
  const drafts = takes.filter((t) => t.status === 'draft')
  const finished = takes.filter((t) => t.status === 'finished')

  // ONE OPEN DRAFT AT A TIME (Paul 23 Oct 2026). The open draft of a scope is CONTINUED, never duplicated: the
  // server also redirects a "start" to it. If several are open (legacy), the one with the most counted wins.
  const openFor = (scoped) => drafts.filter((t) => !!t.scoped === scoped && (!scoped || t.scope_area === 'Curaprox Retail'))
    .sort((a, b) => (b.counted - a.counted) || String(b.started_at || '').localeCompare(String(a.started_at || '')))[0]
  const openFull = openFor(false)
  const openCura = openFor(true)

  function startNew(scopeArea, replace = null) {
    if (pin_mode && !countedBy.trim()) { toast.error('Enter who is counting'); return }
    setStarting(scopeArea ? 'curaprox' : 'full')
    const extra = replace ? { replace: replace.mode, replace_id: replace.id } : {}
    router.post('/stock_takes', { counted_by: countedBy, scope_area: scopeArea || '', ...extra }, {
      onError: () => { toast.error('Could not start the count'); setStarting('') },
      onFinish: () => setStarting(''),
    })
  }

  // "Start new" while a draft is open = an explicit confirm that FINISHES it (it has counts) or DISCARDS it (empty).
  function startNewInstead(open, scopeArea) {
    const msg = open.counted > 0
      ? `Stock take #${open.id} has ${open.counted} of ${open.total} items counted.\n\nFINISH it (it goes to the ordering pull) and start a new count?`
      : `Stock take #${open.id} is empty.\n\nDISCARD it and start a new count?`
    if (!confirm(msg)) return
    startNew(scopeArea, { mode: open.counted > 0 ? 'finish' : 'discard', id: open.id })
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex items-center gap-3">
          <ClipboardCheck className="text-brand-primary" />
          <div>
            <h1 className="text-xl font-semibold text-brand-ink">Stock Take</h1>
            <p className="text-sm text-brand-muted">{active_item_count} active catalogue items</p>
          </div>
        </div>

        {/* Continue the open count, or start a new one (one open draft per scope) */}
        <div className="mb-6 rounded-2xl border border-brand-border bg-white p-4">
          <h2 className="mb-3 font-semibold text-brand-ink">{openFull || openCura ? 'Continue or start a count' : 'Start a new count'}</h2>
          {pin_mode && (
            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-medium text-brand-muted">Counted by</span>
              <input value={countedBy} onChange={(e) => setCountedBy(e.target.value)} placeholder="Your name"
                className="w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm focus:border-brand-primary focus:outline-none sm:max-w-xs" />
            </label>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            {openFull ? (
              <ContinueButton t={openFull} onNew={() => startNewInstead(openFull, null)} disabled={!!starting} />
            ) : (
              <button onClick={() => startNew(null)} disabled={!!starting}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                <Plus size={16} /> {starting === 'full' ? 'Starting…' : `Full clinical count (${active_item_count})`}
              </button>
            )}
            {curaprox_item_count > 0 && (openCura ? (
              <ContinueButton t={openCura} onNew={() => startNewInstead(openCura, 'Curaprox Retail')} disabled={!!starting} secondary />
            ) : (
              <button onClick={() => startNew('Curaprox Retail')} disabled={!!starting}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-brand-primary bg-white px-5 py-2.5 text-sm font-semibold text-brand-primary disabled:opacity-60">
                <Package size={16} /> {starting === 'curaprox' ? 'Starting…' : `Curaprox count (${curaprox_item_count})`}
              </button>
            ))}
          </div>
        </div>

        <Link href="/stock/returns" className="mb-6 flex items-center gap-2 rounded-xl border border-brand-border bg-white px-4 py-3 text-sm font-medium text-brand-ink hover:bg-brand-surface">
          <Undo2 size={16} className="text-brand-primary" /> Returns — lodge a return &amp; track refunds →
        </Link>

        {is_owner && (pending_suggestion_count > 0) && (
          <Link href="/stock/pending" className="mb-6 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
            <PackagePlus size={16} /> {pending_suggestion_count} suggested item(s) waiting for your approval →
          </Link>
        )}

        {drafts.length > 0 && (
          <Section title="In progress — resume">
            {drafts.map((t) => <TakeRow key={t.id} t={t} resume />)}
          </Section>
        )}
        <Section title="Finished counts">
          {finished.length === 0 ? <p className="px-4 py-6 text-center text-sm text-brand-muted">No finished counts yet.</p>
            : finished.map((t) => <TakeRow key={t.id} t={t} />)}
        </Section>
      </div>
    </DashboardLayout>
  )
}

// "Continue stock take, started 09:22 by Dr Eliska (6 of 238 counted)" — the MAIN button while a draft is open.
function ContinueButton({ t, onNew, disabled, secondary }) {
  return (
    <div className="flex flex-1 flex-col gap-1">
      <Link href={`/stock_takes/${t.id}`}
        className={cn('flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-center text-sm font-semibold',
          secondary ? 'border border-brand-primary bg-white text-brand-primary' : 'bg-brand-primary text-white')}>
        <Play size={16} className="shrink-0" />
        <span>Continue {t.scoped ? `${t.scope_label} ` : ''}stock take, started {startedLabel(t.started_at)} by {t.counted_by} ({t.counted} of {t.total} counted)</span>
      </Link>
      <button type="button" onClick={onNew} disabled={disabled}
        className="self-center text-xs font-medium text-brand-muted underline underline-offset-2 disabled:opacity-50">
        Start a new one instead…
      </button>
    </div>
  )
}

function startedLabel(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return String(iso).slice(11, 16)
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return d.toDateString() === new Date().toDateString() ? time : `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-brand-muted">{title}</h2>
      <div className="divide-y divide-brand-border overflow-hidden rounded-2xl border border-brand-border bg-white">{children}</div>
    </div>
  )
}

function TakeRow({ t, resume }) {
  return (
    <Link href={`/stock_takes/${t.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-brand-surface">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-medium text-brand-ink">
          Stock take #{t.id}
          {t.scoped && (
            <span className="rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs font-semibold text-brand-primary">{t.scope_label}</span>
          )}
        </p>
        <p className="text-xs text-brand-muted">
          {t.counted_by} · {(t.finished_at || t.started_at || '').slice(0, 16).replace('T', ' ')}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', resume ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800')}>
          {t.counted}/{t.total}
        </span>
        {resume ? <Play size={16} className="text-brand-primary" /> : <Package size={16} className="text-brand-muted" />}
      </div>
    </Link>
  )
}
